import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Tooltip from 'rc-tooltip';
import { FormattedMessage } from 'react-intl';
import { TwoKeySpinner } from '../../_common';

const StyledInputField = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: ${props => (props.error ? '-15px' : 0)};
  
  input {
    align-self: center;
    height: 31px;
    padding: 4.5px 5px;
    border: 0;
    border-bottom: 1px solid #999;
    border-radius: 0;
    color: #555;
    font-size: 16px;
    line-height: 22px;
    
    &.error {
      border-color: #b51c1c;
    }
    
    &:focus {
      box-shadow: 0 0 0 rgba(0, 0, 0, 0);
    }
    
    &::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
      color: #ccc;
      opacity: 1; /* Firefox */
    }

    &:-ms-input-placeholder { /* Internet Explorer 10-11 */
      color: #ccc;
    }

    &::-ms-input-placeholder { /* Microsoft Edge */
      color: #ccc;
    }
  }
  
  .form-error {
    font-size: 10px;
    color: #b51c1c;
  }
  
  .twokey-spinner {
    position: absolute;
    top: 7px;
    right: 0;
    width: 18px;
    height: 18px;
  }
  
  @media screen and (max-width: 575px) {
    input {
      height: 28px;
    }
  }
`;

const StyledHint = styled.div`
  color: #555;
  font-size: 14px;
  line-height: 22px;
  
  ul {
    margin-bottom: 0;
    padding-left: 15px;
    li {
      &.active {
        color: #17936f;
        font-weight: 500;
      }
    }
  }
`;

export const InputField = ({
  input, label, type, withPasswordTooltip, isMobile, meta: {
    touched, error, warning, asyncValidating,
  }, className, ...rest
}) => {
  const Wrapper = (type === 'password' && withPasswordTooltip) ? Tooltip : React.Fragment;
  const wrapperProps = (type === 'password' && withPasswordTooltip) ? {
    overlayClassName: `twokey-tooltip-wrap auth ${isMobile ? 'top' : 'bottom'}`,
    placement: isMobile ? 'topLeft' : 'bottomLeft',
    animation: 'zoom',
    trigger: ['focus'],
    align: isMobile ? { offset: [0, -10] } : { offset: [0, 10] },
    overlay: (
      <React.Fragment>
        <StyledHint>
          <FormattedMessage id="auth.signup.password_policy" />
          <ul>
            <li className={/[A-Z]/.test(input.value) ? 'active' : ''}>
              <FormattedMessage id="auth.signup.password_policy.upcase" />
            </li>
            <li className={/[a-z]/.test(input.value) ? 'active' : ''}>
              <FormattedMessage id="auth.signup.password_policy.locase" />
            </li>
            <li className={/[0-9]/.test(input.value) ? 'active' : ''}>
              <FormattedMessage id="auth.signup.password_policy.number" />
            </li>
            <li className={/[^A-Za-z0-9]/.test(input.value) ? 'active' : ''}>
              <FormattedMessage id="auth.signup.password_policy.char" />
            </li>
            <li className={input.value.length >= 8 ? 'active' : ''}>
              <FormattedMessage id="auth.signup.password_policy.8chars" />
            </li>
          </ul>
        </StyledHint>
      </React.Fragment>
    ),
  } : {};
  return (
    <StyledInputField className={className} error={touched && error}>
      <Wrapper {...wrapperProps}>
        <input
          className={`form-control ${touched && error ? 'error' : ''}`}
          {...input}
          placeholder={label}
          type={type}
          {...rest}
        />
      </Wrapper>
      {asyncValidating && <TwoKeySpinner />}
      {touched && ((error && <span className="form-error">{error}</span>) || (warning && <span>{warning}</span>))}
    </StyledInputField>
  );
};

InputField.propTypes = {
  isMobile: PropTypes.bool,
  handleValidating: PropTypes.bool,
  withPasswordTooltip: PropTypes.bool,
  label: PropTypes.string,
  type: PropTypes.string,
  className: PropTypes.string,
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
};

export default InputField;
